import type { CSSProperties } from 'react';
import './DomainCard.css';

interface Props {
  name: string;
  color: string;
  count: number;
  style?: CSSProperties;
}

/**
 * Domain header chip — fixed-pixel HTML overlay anchored INSIDE the
 * top edge of a Cytoscape compound node. Stays at constant size
 * regardless of graph zoom (Cytoscape labels would scale and become
 * unreadable when the owner zooms out to inspect cross-area edges).
 */
export function DomainCard({ name, color, count, style }: Props) {
  const rgba = toRgba(color);
  const cssVars: CSSProperties & { [key: string]: string | number } = {
    '--domain-color': rgba,
    '--domain-color-fill': withAlpha(rgba, 0.1),
    '--domain-color-border': withAlpha(rgba, 0.4),
    '--domain-color-bright': withAlpha(rgba, 0.95),
    '--domain-color-text': withAlpha(rgba, 0.95),
    ...style,
  };

  return (
    <div className="domain-card" style={cssVars}>
      <div className="domain-card__beam" />
      <span className="domain-card__name">{name.toUpperCase()}</span>
      <span className="domain-card__count">
        {count} {count === 1 ? 'page' : 'pages'}
      </span>
    </div>
  );
}

function toRgba(color: string): string {
  if (color.startsWith('rgba') || color.startsWith('rgb')) return color;
  if (!color.startsWith('#')) return color;
  const hex = color.replace('#', '');
  const bigint = parseInt(
    hex.length === 3
      ? hex.split('').map((c) => c + c).join('')
      : hex,
    16,
  );
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},1)`;
}

function withAlpha(rgba: string, alpha: number): string {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return rgba;
  return `rgba(${match[1]},${match[2]},${match[3]},${alpha})`;
}
